import type { CopilotToolCallStatus } from '@customerio/hydra-copilot';
import { themeVars } from '@customerio/pluma-components/css/theme-tokens';
import { Box, Icon, Spinner, Text } from '@customerio/pluma-components/react';
import { ShimmerText } from '@/components/feedback/shimmer-text';
import { isEmptyString } from '@/utils/string';
import { lowerCase, upperFirst } from 'lodash-es';

export function ToolCallSet({ children }: { children: React.ReactNode }) {
  return (
    <Box
      display="flex"
      flexDirection="column"
      overflow="hidden"
      p="075"
      px="100"
      gap="100"
      style={{
        border: `${themeVars.border['width-0125']} solid ${themeVars.color['border-minimal']}`,
        borderRadius: themeVars.radius['6'],
      }}
    >
      {children}
    </Box>
  );
}

export function ToolCall({
  name,
  status,
  error,
  summary,
}: {
  name: string;
  status: CopilotToolCallStatus;
  error?: string | null;
  summary?: {
    executing: string;
    success: string;
  };
}) {
  return (
    <Box display="flex" gap="075">
      <Box
        style={{ height: 16, width: 16, marginTop: 2 }}
        flexShrink="0"
        display="flex"
        alignItems="center"
        justifyContent="flex-start"
      >
        {status === 'executing' ? (
          <Spinner style={{ scale: '0.75' }} />
        ) : status === 'success' ? (
          <Icon color="success" name="check-circle" size="fill" />
        ) : (
          <Icon color="critical" name="error" size="fill" />
        )}
      </Box>
      <Box display="flex" flexDirection="column" gap="025" flexGrow="1">
        <Text as={status === 'executing' ? ShimmerText : 'span'} size="xs">
          {(() => {
            if (status === 'executing') {
              const executingText = trimPunctuation(summary?.executing);
              if (executingText.length > 0) {
                return executingText + '…';
              }
            } else if (status === 'success') {
              const successText = trimPunctuation(summary?.success);
              if (successText.length > 0) {
                return successText;
              }
            }
            return upperFirst(lowerCase(name));
          })()}
        </Text>
        {status === 'error' ? (
          <Text color="critical" size="xxs">
            {typeof error === 'string' ? error : 'An unknown error occurred while calling this tool.'}
          </Text>
        ) : null}
      </Box>
    </Box>
  );
}

function trimPunctuation(text?: string) {
  if (isEmptyString(text)) {
    return '';
  }

  return text.trim().replace(/\.+$/, '');
}
